'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';

const headlines = [
  'financial modelling',
  'budgeting solutions',
  'data analytics',
  'performance appraisal',
];

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setHeadlineIdx((i) => (i + 1) % headlines?.length);
        setFading(false);
      }, 350);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="home"
      aria-label="Celsoft Corporation hero"
      className="relative overflow-hidden min-h-screen flex flex-col justify-center"
      style={{
        background: 'linear-gradient(135deg, #071318 0%, #0d2229 30%, #1a3a44 65%, #1f525d 100%)',
      }}
    >
      {/* Animated gradient orbs */}
      <div
        className="absolute top-[-10%] right-[-5%] w-[700px] h-[700px] rounded-full opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(141,197,62,0.25) 0%, rgba(31,82,93,0.15) 40%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />
      <div
        className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(108,45,48,0.3) 0%, transparent 70%)',
          filter: 'blur(100px)',
        }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-10 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(31,82,93,0.4) 0%, transparent 60%)',
          filter: 'blur(120px)',
        }}
      />
      {/* Dot grid pattern */}
      <div className="absolute inset-0 pattern-dots opacity-40 pointer-events-none" />
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, transparent, #8dc53e 30%, #a8d85a 70%, transparent)' }} />
      {/* Floating geometric shapes */}
      <div className="absolute top-[15%] right-[8%] w-24 h-24 rounded-2xl border border-white/10 rotate-12 animate-float pointer-events-none" style={{ animationDelay: '0s' }} />
      <div className="absolute top-[60%] right-[15%] w-14 h-14 rounded-xl border border-[#8dc53e]/20 rotate-45 animate-float pointer-events-none" style={{ animationDelay: '1.5s' }} />
      <div className="absolute bottom-[20%] left-[5%] w-20 h-20 rounded-2xl border border-white/8 -rotate-12 animate-float pointer-events-none" style={{ animationDelay: '0.8s' }} />
      {/* Main content */}
      <div className="relative z-10 max-w-[1280px] mx-auto px-6 lg:px-10 py-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-16 items-center">
          {/* Left column */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full mb-8" style={{ background: 'rgba(141,197,62,0.12)', border: '1px solid rgba(141,197,62,0.3)' }}>
              <span className="w-2 h-2 rounded-full bg-[#8dc53e] animate-pulse-ring" />
              <span className="text-xs font-semibold text-[#8dc53e] tracking-widest uppercase">
                Trusted across Africa since 2006
              </span>
            </div>

            {/* Main headline */}
            <h1 className="font-bold text-white mb-6 hero-text-shadow" style={{ fontSize: 'clamp(2.6rem, 5.5vw, 5rem)', lineHeight: 1.05, letterSpacing: '-0.025em' }}>
              The home of
              <span
                className="block"
                style={{
                  color: '#8dc53e',
                  opacity: mounted && !fading ? 1 : 0,
                  transform: mounted && !fading ? 'translateY(0)' : 'translateY(12px)',
                  transition: 'opacity 0.35s ease, transform 0.35s ease',
                  minHeight: '1.1em',
                }}
              >
                {headlines?.[headlineIdx]}
              </span>
              <span className="block">for Africa.</span>
            </h1>

            <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-[520px]" style={{ fontWeight: 300 }}>
              We leverage Quantrix — the world&apos;s leading financial modelling platform — to build custom solutions for organizations across Africa.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4">
              <Link
                href="#solutions"
                className="group inline-flex items-center gap-3 px-8 py-4 font-bold rounded-xl text-[#0d2229] text-sm tracking-wide uppercase transition-all duration-200 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #8dc53e, #a8d85a)', boxShadow: '0 8px 30px rgba(141,197,62,0.35)' }}
              >
                Explore Solutions
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="group-hover:translate-x-1 transition-transform">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </Link>
              <Link
                href="#contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white text-sm transition-all duration-200 active:scale-95 hover:bg-white/15"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}
              >
                Book a Free Demo
              </Link>
            </div>
          </div>

          {/* Right column — floating card stack */}
          <div className="hidden lg:block relative">
            {/* Main card */}
            <div
              className="relative rounded-2xl p-6 z-10"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-xs text-white/50 uppercase tracking-widest mb-1">Live Dashboard</p>
                  <p className="text-white font-bold text-lg">Budget Overview 2026</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-[#8dc53e]/20 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8dc53e" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                </div>
              </div>

              {/* Mini bar chart */}
              <div className="flex items-end gap-2 h-20 mb-5">
                {[65, 80, 55, 90, 70, 95, 75]?.map((h, i) => (
                  <div key={i} className="flex-1 rounded-t-md transition-all" style={{ height: `${h}%`, background: i === 5 ? '#8dc53e' : 'rgba(141,197,62,0.25)' }} />
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3" style={{ background: 'rgba(141,197,62,0.12)', border: '1px solid rgba(141,197,62,0.2)' }}>
                  <p className="text-xs text-white/50 mb-1">Departments</p>
                  <p className="text-xl font-bold text-[#8dc53e]">24</p>
                </div>
                <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="text-xs text-white/50 mb-1">Consolidated</p>
                  <p className="text-xl font-bold text-white">100%</p>
                </div>
              </div>
            </div>

            {/* Floating badge — top right */}
            <div
              className="absolute -top-4 -right-4 rounded-xl px-4 py-3 z-20 animate-float"
              style={{ background: 'linear-gradient(135deg, #6c2d30, #8a3a3e)', border: '1px solid rgba(255,255,255,0.15)', animationDelay: '1s' }}
            >
              <p className="text-xs text-white/70 mb-0.5">Satisfaction</p>
              <p className="text-2xl font-bold text-white">97%</p>
            </div>

            {/* Floating badge — bottom left */}
            <div
              className="absolute -bottom-4 -left-4 rounded-xl px-4 py-3 z-20 animate-float"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(12px)', animationDelay: '0.5s' }}
            >
              <p className="text-xs text-white/60 mb-0.5">Organizations</p>
              <p className="text-2xl font-bold text-white">280+</p>
            </div>

            {/* Quantrix powered badge */}
            <div
              className="absolute -bottom-4 right-8 rounded-xl px-4 py-2.5 z-20 flex items-center gap-2"
              style={{ background: 'rgba(31,82,93,0.8)', border: '1px solid rgba(141,197,62,0.3)', backdropFilter: 'blur(12px)' }}
            >
              <div className="w-2 h-2 rounded-full bg-[#8dc53e]" />
              <p className="text-xs text-white font-semibold">Powered by Quantrix</p>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(7,19,24,0.6))' }} />
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/30">
        <div className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 rounded-full bg-white/40 animate-bounce" />
        </div>
        <span className="text-[10px] font-medium tracking-widest uppercase">Scroll</span>
      </div>
    </section>
  );
}